import React from 'react';

export interface HelpSection {
  id: string;
  title: string;
  keywords: string[];
  content: React.ReactNode;
  subsections?: HelpSection[];
}

export const helpSections: HelpSection[] = [
  {
    id: 'introduction',
    title: 'Beep User Guide',
    keywords: ['intro', 'overview', 'what is beep', 'adb', 'android', 'device settings'],
    content: (
      <>
        <p>
          Beep is a macOS desktop application for viewing Android device information and running selected ADB actions from a graphical interface.
        </p>
        <p>
          ADB, short for Android Debug Bridge, is the command-line tool Android devices use for computer-to-device communication. Beep depends on ADB. If ADB is not available or the configured path is wrong, Beep cannot load device data or run actions.
        </p>
        <h3 className="help-subsection-title">What Beep Is For</h3>
        <p>Use Beep when you want to:</p>
        <ul>
          <li>check which Android device is connected</li>
          <li>inspect device details such as hardware, system, battery, and storage information</li>
          <li>browse app components such as activities, services, broadcasts, and permissions</li>
          <li>browse and edit Android device settings from the settings tables</li>
          <li>inspect installed packages before taking action on them</li>
          <li>copy ADB commands instead of writing them by hand</li>
          <li>save repeatable device actions as automation scripts</li>
        </ul>
        <p>
          Beep is most useful for technical users, testers, and support or development workflows. Some panels expose advanced Android concepts. If an action affects the device, this guide calls that out directly.
        </p>
      </>
    ),
  },
  {
    id: 'quick-reference',
    title: 'Quick Reference',
    keywords: ['quick', 'reference', 'task', 'panel', 'where'],
    content: (
      <>
        <p>If you want to do this, go here:</p>
        <table className="help-table">
          <thead>
            <tr>
              <th>Task</th>
              <th>Open</th>
            </tr>
          </thead>
          <tbody>
            <tr>
              <td>Set up ADB so Beep can work</td>
              <td><strong>Settings</strong> or <strong>Configure ADB</strong> at startup</td>
            </tr>
            <tr>
              <td>Confirm which device is selected</td>
              <td>top bar <strong>device selector</strong></td>
            </tr>
            <tr>
              <td>Check hardware, battery, storage, modem, GNSS, NFC, or disk health details</td>
              <td><strong>Device Info</strong></td>
            </tr>
            <tr>
              <td>Start an app screen manually</td>
              <td><strong>Activities</strong></td>
            </tr>
            <tr>
              <td>Stop, clear, or uninstall an app</td>
              <td><strong>Packages</strong></td>
            </tr>
            <tr>
              <td>Inspect Android system property values</td>
              <td><strong>Properties</strong></td>
            </tr>
            <tr>
              <td>Review services and try starting one</td>
              <td><strong>Services</strong></td>
            </tr>
            <tr>
              <td>Review broadcasts and prepare a broadcast command</td>
              <td><strong>Broadcasts</strong></td>
            </tr>
            <tr>
              <td>See which permissions an app declares</td>
              <td><strong>Permissions</strong></td>
            </tr>
            <tr>
              <td>Save repeated actions as a reusable script</td>
              <td><strong>Automation</strong></td>
            </tr>
            <tr>
              <td>Inspect or change an Android setting, such as airplane mode</td>
              <td><strong>Settings</strong> in the sidebar</td>
            </tr>
          </tbody>
        </table>
      </>
    ),
  },
  {
    id: 'before-you-start',
    title: 'Before You Start',
    keywords: ['requirements', 'setup', 'macos', 'adb', 'install', 'gatekeeper', 'usb debugging', 'authorize'],
    content: (
      <>
        <p>To use Beep, you need:</p>
        <ul>
          <li>a macOS computer</li>
          <li>the Android Debug Bridge (<code className="help-code">adb</code>) executable installed on your Mac</li>
          <li>at least one Android device available through ADB</li>
          <li>USB debugging authorized for your Mac on that device: unlock the device, connect it, and accept the <strong>Allow USB debugging</strong> prompt when it appears</li>
        </ul>
        <p>
          If ADB is installed but Beep cannot find it, a typical location on macOS is <code className="help-code">~/Library/Android/sdk/platform-tools/adb</code>. Use <strong>Browse for ADB</strong> to point Beep to that file.
        </p>
        <p>
          When Beep starts, it checks the configured ADB path. If the path is missing or invalid, Beep opens <strong>Configure ADB</strong> and waits for you to fix it before it opens the main workbench.
        </p>
        <h3 className="help-subsection-title">Install Beep on macOS</h3>
        <p>Beep is distributed as a DMG file. Beep is not signed with an Apple Developer ID, so macOS Gatekeeper blocks it on first launch. To install:</p>
        <ol>
          <li>Open the DMG file.</li>
          <li>Drag <strong>beep.app</strong> to the <strong>Applications</strong> folder.</li>
          <li>Right-click (or Control-click) <strong>beep.app</strong> in Applications and select <strong>Open</strong>.</li>
          <li>Click <strong>Open</strong> when macOS asks for confirmation.</li>
        </ol>
        <p>Alternatively, run this command in Terminal after copying the app to Applications:</p>
        <p><code className="help-code">xattr -cr /Applications/beep.app</code></p>
        <p>This removes the quarantine attribute so Beep runs normally. You only need to do this once.</p>
      </>
    ),
  },
  {
    id: 'configuration',
    title: 'Configuration',
    keywords: ['adb', 'settings', 'configure', 'path', 'updates', 'appearance', 'privacy', 'logging', 'safety', 'update failed', 'restart required', 'release notes'],
    content: (
      <>
        <h3 className="help-subsection-title">Open Beep and Configure ADB</h3>
        <p>If Beep cannot use ADB yet, the first screen shows <strong>Configure ADB</strong>.</p>
        <p>In <strong>ADB Configuration</strong>:</p>
        <ul>
          <li><strong>ADB Path</strong> shows the path to the <code className="help-code">adb</code> executable Beep will use.</li>
          <li><strong>Search for ADB</strong> asks Beep to look for ADB automatically. Use this first if ADB is already installed and you do not know where it is.</li>
          <li><strong>Browse for ADB</strong> lets you choose the executable yourself. Use this when auto-discovery does not find the correct file or when you want to point Beep to a specific ADB installation.</li>
        </ul>
        <p>If the path is invalid, Beep shows an error in the same section. Beep does not continue until the path is valid.</p>

        <h3 className="help-subsection-title">Settings</h3>
        <p>Use <strong>Settings</strong> to control how Beep finds ADB and how the app behaves.</p>

        <h4 className="help-subsubsection-title">ADB Configuration</h4>
        <p>Use this section whenever Beep cannot detect a device, cannot load data, or opened in <strong>Configure ADB</strong> mode at startup.</p>
        <ul>
          <li><strong>ADB Path</strong> sets the exact executable Beep uses for all ADB commands.</li>
          <li><strong>Search for ADB</strong> attempts automatic discovery.</li>
          <li><strong>Browse for ADB</strong> opens a file picker so you can choose the executable yourself.</li>
        </ul>

        <h4 className="help-subsubsection-title">Updates</h4>
        <p>Beep checks for updates automatically during startup, after ADB is configured. If the check cannot run, Beep continues without a message and tries again the next time you open the app.</p>
        <p>When an update is available, the dialog shows the new version, its size, and the release notes for every version between yours and the new one, grouped by version.</p>
        <p>Choose <strong>Update Now</strong> to start the download or <strong>Later</strong> to continue with the current version. Some releases are marked for priority rollout and recommended for immediate installation, but <strong>Later</strong> is still available.</p>
        <p>During the download, Beep shows progress, speed, and estimated time. You can select <strong>Cancel</strong> without losing the part already downloaded; Beep resumes the partial download the next time you update.</p>
        <p>Beep verifies the update signature and file checksum before installing. During installation, keep Beep open. When installation finishes, Beep shows <strong>Restart Required</strong>; select <strong>Restart Now</strong> to reopen the app with the updated version.</p>
        <p>If the download fails, Beep shows <strong>Download Failed</strong> with the reason. Select <strong>Retry Download</strong> to try again or <strong>Continue Current Version</strong> to keep using the installed version. Some failures cannot be retried automatically.</p>
        <p>If installation fails, Beep keeps the downloaded file on your Mac so you can retry or install it manually later.</p>

        <h4 className="help-subsubsection-title">Appearance</h4>
        <ul>
          <li><strong>Dark Mode</strong> switches the application theme.</li>
        </ul>

        <h4 className="help-subsubsection-title">Privacy</h4>
        <ul>
          <li><strong>Enable Analytics</strong> turns analytics collection on or off.</li>
        </ul>
        <p>Turning analytics off does not disable crash and error monitoring. Error monitoring is separate, and Beep removes sensitive values such as device serial numbers, local file paths, and script contents before sending error reports.</p>

        <h4 className="help-subsubsection-title">Safety</h4>
        <ul>
          <li><strong>Confirm Destructive Actions</strong> controls whether Beep asks for confirmation before running high-impact actions.</li>
        </ul>
        <p>When this setting is on, Beep shows a confirmation dialog before these actions:</p>
        <ul>
          <li><strong>Reboot Device</strong></li>
          <li><strong>Clear Data</strong></li>
          <li><strong>Uninstall</strong></li>
        </ul>
        <p>When this setting is off, Beep runs those actions immediately after you click them.</p>
        <p><strong>Force Stop</strong> is not covered by this setting and does not show a confirmation dialog.</p>

        <h4 className="help-subsubsection-title">Logging</h4>
        <p>Use <strong>Logging</strong> when you need more or less detail about the ADB commands Beep runs.</p>
        <ul>
          <li><strong>Enable Logging</strong> turns logging on or off.</li>
          <li><strong>ADB Logging Level</strong> controls how much detail is captured:
            <ul>
              <li><strong>Off</strong>: do not keep ADB logs</li>
              <li><strong>Errors Only</strong>: record failures without full command output</li>
              <li><strong>Full</strong>: record full ADB activity</li>
            </ul>
          </li>
        </ul>
        <p>If you are troubleshooting a device or command problem, <strong>Errors Only</strong> or <strong>Full</strong> can be useful. For normal use, <strong>Off</strong> or <strong>Errors Only</strong> is usually enough.</p>
      </>
    ),
  },
  {
    id: 'main-window',
    title: 'Main Window',
    keywords: ['window', 'navigation', 'device selector', 'refresh', 'sidebar', 'unauthorized', 'offline', 'banner'],
    content: (
      <>
        <p>After setup, Beep opens the main workbench.</p>
        <p>The top bar contains:</p>
        <ul>
          <li><strong>device selector</strong>, which chooses the Android device Beep will inspect and control</li>
          <li><strong>Refresh devices</strong>, which reloads the connected device list</li>
          <li><strong>Open settings</strong>, which opens <strong>Settings</strong></li>
          <li><strong>Close application</strong>, which closes Beep</li>
        </ul>
        <p>The left sidebar contains the main work areas:</p>
        <ul>
          <li>Device Info</li>
          <li>Activities</li>
          <li>Packages</li>
          <li>Properties</li>
          <li>Services</li>
          <li>Broadcasts</li>
          <li>Permissions</li>
          <li>Automation</li>
          <li>Settings</li>
        </ul>
        <p>
          The sidebar <strong>Settings</strong> opens the Android device settings browser. It is not the same as <strong>Open settings</strong> in the top bar, which opens Beep's own application settings, or the <strong>Device Settings</strong> button in <strong>Device Info</strong>, which opens the Settings app on the device itself.
        </p>
        <p>Most panels require a selected device. If no device is selected, Beep shows an empty state such as <strong>Select a device first</strong> or <strong>No device selected</strong>.</p>
        <p>
          When a device is connected but not ready, Beep shows a banner above the panels explaining the problem, such as a device that is offline or waiting for USB debugging authorization. The banner includes the next step to fix it, usually accepting the prompt on the device or selecting <strong>Refresh devices</strong>. See <strong>Troubleshooting</strong> for each connection state.
        </p>
      </>
    ),
  },
  {
    id: 'device-info',
    title: 'Device Info',
    keywords: ['device', 'hardware', 'system', 'battery', 'storage', 'screenshot', 'reboot', 'modem', 'gnss', 'gps', 'nfc', 'disk', 'emmc', 'ufs', 'health', 'cycles', 'selinux', 'treble'],
    content: (
      <>
        <p>Use <strong>Device Info</strong> when you want a quick health and identity check for the selected device.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>confirm you selected the correct device</li>
          <li>check hardware, system, and security details</li>
          <li>review battery status and health</li>
          <li>inspect the storage chip, disk health, and partition breakdown</li>
          <li>view modem, GNSS, and NFC hardware details</li>
          <li>take a screenshot</li>
          <li>reboot the device</li>
        </ul>
        <p>If device information is available, the panel shows a tab bar with seven tabs:</p>
        <ul>
          <li>Hardware</li>
          <li>System</li>
          <li>Battery</li>
          <li>Storage</li>
          <li>Modem</li>
          <li>GNSS</li>
          <li>NFC</li>
        </ul>
        <p>Storage, Modem, GNSS, and NFC appear only when Beep can detect that information on the connected device. A missing tab usually means the device does not have or did not report that hardware, not that something failed.</p>
        <p>These tabs are for inspection, not editing. Many values can be copied to the clipboard for use in reports or commands.</p>
        <p>The action buttons at the top of the panel are:</p>
        <ul>
          <li><strong>Refresh</strong>: reloads device information</li>
          <li><strong>Screenshot</strong>: asks the selected device to take a screenshot</li>
          <li><strong>Reboot Device</strong>: reboots the selected device</li>
          <li><strong>Device Settings</strong>: opens the system settings screen on the Android device itself (not Beep settings)</li>
        </ul>
        <div className="help-note help-note-important">
          <strong>Important:</strong>
          <ul>
            <li><strong>Reboot Device</strong> restarts the device immediately. Any work in progress on the device is interrupted. If <strong>Confirm Destructive Actions</strong> is enabled in <strong>Settings</strong>, Beep asks for confirmation first.</li>
            <li><strong>Screenshot</strong> captures a screenshot and opens it in macOS Preview. The image is saved to a temporary file on your Mac.</li>
            <li><strong>Device Settings</strong> opens the Android device's own settings interface, equivalent to opening the Settings app on the device. This is not a Beep settings screen and is also not the sidebar <strong>Settings</strong> panel.</li>
          </ul>
        </div>

        <h3 className="help-subsection-title">Hardware tab</h3>
        <p>The <strong>Hardware</strong> tab shows device identity and physical specifications:</p>
        <ul>
          <li>a summary card with the device model name and serial number</li>
          <li><strong>RAM</strong>, <strong>Screen Size</strong>, and <strong>Screen Resolution</strong> highlight fields</li>
          <li>identifiers: <strong>Brand</strong>, <strong>Processor</strong>, and <strong>Architecture</strong></li>
        </ul>

        <h3 className="help-subsection-title">System tab</h3>
        <p>The <strong>System</strong> tab shows Android version, build, security, platform, kernel, and runtime information, organized in sub-sections:</p>
        <ul>
          <li><strong>Version</strong>: Android version with API level and the main security patch date</li>
          <li><strong>Build</strong>: build ID, firmware, build type and tags badges, fingerprint, and build dates</li>
          <li><strong>Security</strong>: <strong>SELinux</strong>, <strong>Verified Boot</strong>, device lock state, and verity badges</li>
          <li><strong>Security Patches</strong>: patch dates for system, vendor, boot, kernel, and OEM partitions when reported</li>
          <li><strong>Platform</strong>: <strong>Treble</strong>, <strong>A/B Update</strong>, and dynamic partition badges</li>
          <li><strong>Kernel</strong>: kernel version, build string, and boot command line when reported</li>
          <li><strong>Runtime</strong>: uptime, boot reason, and boot counts; crash-related boot reasons are highlighted as warnings</li>
          <li><strong>OS Settings</strong>: developer options, ADB enabled, and airplane mode badges</li>
          <li><strong>Diagnostics</strong>: counts of detected services and enabled or disabled packages</li>
        </ul>

        <h3 className="help-subsection-title">Battery tab</h3>
        <p>The <strong>Battery</strong> tab shows charge level, health, charging status, and hardware details:</p>
        <ul>
          <li><strong>Level</strong> with a progress bar, plus <strong>Charging Status</strong> and <strong>Health</strong> badges</li>
          <li><strong>Cycles</strong>, <strong>Capacity</strong> in mAh, and <strong>Temperature</strong> highlight fields</li>
          <li>hardware identifiers: <strong>Voltage</strong>, <strong>Technology</strong>, <strong>Charger Voltage</strong>, <strong>Driver</strong>, and <strong>Charger IC</strong></li>
        </ul>
        <p>Cycles, Capacity, Charger Voltage, Driver, and Charger IC depend on kernel support and device reporting. When the device does not expose a value, Beep shows <strong>N/A</strong> instead of a number.</p>

        <h3 className="help-subsection-title">Storage tab</h3>
        <p>The <strong>Storage</strong> tab shows disk hardware and how storage space is used:</p>
        <ul>
          <li>a usage overview with <strong>Total</strong>, <strong>Used</strong>, and <strong>Free</strong> amounts</li>
          <li><strong>Storage Chip</strong>: vendor and interface, such as Samsung eMMC or UFS, plus model, serial, and firmware for eMMC chips</li>
          <li><strong>Health Status</strong>: a <strong>Good</strong>, <strong>Fair</strong>, or <strong>Worn</strong> badge based on the chip's lifetime estimate, available for eMMC chips that report it</li>
          <li><strong>Partitions</strong>: used space per category, including user data, system, bootloader, and cache</li>
        </ul>
        <p>For UFS chips, Beep shows only the interface type and capacity because UFS exposes fewer diagnostic fields than eMMC. When the device does not report a chip detail, Beep shows <strong>N/A</strong> instead of a value.</p>

        <h3 className="help-subsection-title">Modem tab</h3>
        <p>The <strong>Modem</strong> tab shows cellular modem hardware and firmware. It appears only when Beep can detect modem information:</p>
        <ul>
          <li><strong>Modem State</strong> badge, such as <strong>ONLINE</strong>, <strong>OFFLINE</strong>, or <strong>CRASHED</strong>, and the recorded crash count</li>
          <li><strong>Identification</strong>: manufacturer, SoC model and platform, and baseband type</li>
          <li><strong>Firmware</strong>: firmware name and date, baseband version, and RIL version</li>
          <li><strong>Subsystems</strong>: state and crash count per modem subsystem when reported</li>
          <li><strong>SIM Configuration</strong>: dual SIM mode, SIM slots, VoLTE, and default network when reported</li>
          <li><strong>Radio Technologies</strong>: badges such as <strong>GSM</strong>, <strong>WCDMA</strong>, <strong>LTE</strong>, and <strong>NR/5G</strong></li>
        </ul>

        <h3 className="help-subsection-title">GNSS tab</h3>
        <p>The <strong>GNSS</strong> tab shows GPS and satellite positioning hardware. It appears only when Beep can detect GNSS information:</p>
        <ul>
          <li><strong>Identification</strong>: GNSS HAL service and versions, service status, and whether the receiver is integrated with the modem</li>
          <li><strong>Configuration</strong>: capabilities, SUPL server and version, LPP profiles, and debug level</li>
          <li><strong>Constellations</strong>: supported satellite systems as badges, such as <strong>GPS</strong>, <strong>GLONASS</strong>, <strong>Galileo</strong>, and <strong>BeiDou</strong></li>
          <li><strong>Antenna</strong>: carrier frequency and calibration values; on Qualcomm devices this usually requires an active GPS fix, so the tab may show a hint asking you to get a fix first</li>
          <li><strong>Health</strong>: <strong>TTFF</strong>, visible and in-use satellite counts, and radio state when reported</li>
        </ul>

        <h3 className="help-subsection-title">NFC tab</h3>
        <p>The <strong>NFC</strong> tab shows Near Field Communication hardware. It appears only when Beep can detect NFC hardware:</p>
        <ul>
          <li><strong>Identification</strong>: chip model, driver, and bus interface such as <strong>I2C</strong> or <strong>SPI</strong></li>
          <li><strong>Stack</strong>: the <strong>NCI</strong> version when reported</li>
          <li><strong>Secure Element</strong>: HAL version, secure element instances such as SIM-based or embedded, the default AID route, and Mifare support</li>
        </ul>
      </>
    ),
  },
  {
    id: 'activities',
    title: 'Activities',
    keywords: ['activities', 'launch', 'activity', 'launcher', 'debug', 'deep link'],
    content: (
      <>
        <p>An activity is an Android screen or entry point. Use <strong>Activities</strong> when you want to open a specific screen inside an app or copy the ADB command needed to start that screen.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>open a known screen for testing</li>
          <li>confirm which activity launches an app</li>
          <li>copy a launch command for later use</li>
        </ul>
        <p>Each package appears as a group card. Inside each group, Beep lists the activities it found.</p>
        <p>The toolbar includes:</p>
        <ul>
          <li>a search field with the placeholder <strong>Search activities (min 3 chars)...</strong></li>
          <li><strong>Refresh</strong></li>
        </ul>
        <p>For each activity, the available actions are:</p>
        <ul>
          <li><strong>Launch</strong></li>
          <li><strong>Launch with Debug</strong></li>
          <li><strong>Copy Command</strong></li>
        </ul>
        <p>What these actions mean:</p>
        <ul>
          <li><strong>Launch</strong> opens the selected activity normally.</li>
          <li><strong>Launch with Debug</strong> is mainly for debugging and development workflows. Use it when you want to start the activity in a debug-friendly way rather than as normal app use.</li>
          <li><strong>Copy Command</strong> copies the ADB command for starting that activity.</li>
        </ul>
        <div className="help-note help-note-important">
          <strong>Important:</strong>
          <ul>
            <li>If <strong>Launch</strong> or <strong>Launch with Debug</strong> appears to do nothing, the app or Android may be blocking that launch path, the activity may require a specific app state, or the selected activity may not be startable from outside the app.</li>
          </ul>
        </div>
      </>
    ),
  },
  {
    id: 'packages',
    title: 'Packages',
    keywords: ['packages', 'apps', 'uninstall', 'clear data', 'force stop', 'signature', 'version'],
    content: (
      <>
        <p>A package is an installed Android app identified by its package name, such as <code className="help-code">com.example.app</code>. Use <strong>Packages</strong> when you need to inspect an app before acting on it, confirm build identity, or run package-level actions such as stopping or uninstalling it.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>confirm the exact package name</li>
          <li>check whether an app is a system app, debug build, or disabled</li>
          <li>confirm app version information</li>
          <li>view signature information before comparing builds</li>
          <li>stop, clear, or uninstall an app</li>
        </ul>
        <p>Each package card shows:</p>
        <ul>
          <li>the package name</li>
          <li>the version</li>
          <li>the version code, when available</li>
          <li>the target SDK, when available</li>
          <li>badges such as <strong>System</strong>, <strong>Debug</strong>, or <strong>Disabled</strong></li>
        </ul>
        <p>Terms used here:</p>
        <ul>
          <li><strong>target SDK</strong> is the Android API level the app targets. It helps you understand what Android version behavior the app was built against.</li>
          <li><strong>signature</strong> or <strong>signing certificate</strong> identifies who signed the app build. This is useful when you need to confirm whether two builds came from the same signing source.</li>
        </ul>
        <p>The toolbar includes:</p>
        <ul>
          <li>a search field with the placeholder <strong>Search packages (min 3 chars)...</strong></li>
          <li><strong>Refresh</strong></li>
          <li><strong>User only</strong></li>
          <li><strong>Enabled only</strong></li>
          <li><strong>Debug only</strong></li>
        </ul>
        <p>Use these filters to narrow the list:</p>
        <ul>
          <li><strong>User only</strong> hides system packages and keeps user-installed apps</li>
          <li><strong>Enabled only</strong> shows only apps that are currently enabled</li>
          <li><strong>Debug only</strong> shows only debug builds</li>
        </ul>
        <p>For each package, the available actions are:</p>
        <ul>
          <li><strong>View Signature</strong></li>
          <li><strong>Copy Package Name</strong></li>
          <li><strong>Force Stop</strong></li>
          <li><strong>Clear Data</strong></li>
          <li><strong>Uninstall</strong> for non-system apps</li>
        </ul>
        <p>What these actions mean:</p>
        <ul>
          <li><strong>View Signature</strong> extracts the app's signing information so you can confirm which certificate signed it.</li>
          <li><strong>Copy Package Name</strong> copies the exact package identifier for later use.</li>
          <li><strong>Force Stop</strong> stops the app's current process.</li>
          <li><strong>Clear Data</strong> removes the app's stored data on the device.</li>
          <li><strong>Uninstall</strong> removes the app from the device.</li>
        </ul>
        <div className="help-note help-note-important">
          <strong>Important:</strong>
          <ul>
            <li><strong>Force Stop</strong> is usually temporary. It stops the current app process, but it does not remove the app or erase its data.</li>
            <li><strong>Clear Data</strong> is more destructive. It removes the app's local data, which may include sign-in state, settings, and cached content. This is not something you can undo from Beep. If <strong>Confirm Destructive Actions</strong> is enabled in <strong>Settings</strong>, Beep asks for confirmation first.</li>
            <li><strong>Uninstall</strong> removes the app itself from the device. This action is not shown for system apps. If <strong>Confirm Destructive Actions</strong> is enabled in <strong>Settings</strong>, Beep asks for confirmation first.</li>
          </ul>
        </div>

        <h3 className="help-subsection-title">Inspect a package before acting on it</h3>
        <p>If you are not sure which action to use:</p>
        <ol>
          <li>Open <strong>Packages</strong>.</li>
          <li>Search for the app by package name.</li>
          <li>Review the version, badges, and whether it is a system app.</li>
          <li>If build identity matters, select <strong>View Signature</strong> first.</li>
          <li>Choose the action:
            <ul>
              <li>use <strong>Force Stop</strong> if you only need to stop the app now</li>
              <li>use <strong>Clear Data</strong> if you need a clean app state</li>
              <li>use <strong>Uninstall</strong> if you need the app removed from the device</li>
            </ul>
          </li>
        </ol>
      </>
    ),
  },
  {
    id: 'properties',
    title: 'Properties',
    keywords: ['properties', 'system', 'configuration', 'device properties'],
    content: (
      <>
        <p>A system property is a device-reported key-value setting. Use <strong>Properties</strong> when you need to look up low-level device or Android configuration values.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>confirm a specific device property value</li>
          <li>compare property values across devices</li>
          <li>search for system identifiers or configuration flags</li>
        </ul>
        <p>This panel is for lookup only. It does not change the device.</p>
        <p>The top bar includes:</p>
        <ul>
          <li>a search field with the placeholder <strong>Search properties (2+ chars)...</strong></li>
          <li><strong>Refresh</strong></li>
        </ul>
        <p>When your search is at least two characters long, Beep filters the list. If nothing matches, the panel shows <strong>No properties match your search</strong>.</p>
      </>
    ),
  },
  {
    id: 'services',
    title: 'Services',
    keywords: ['services', 'start', 'stop', 'android services', 'system services', 'full scan'],
    content: (
      <>
        <p>A service is an Android component that runs work without opening a normal app screen. Use <strong>Services</strong> when you need to inspect service availability, confirm whether a service is enabled or exported, or try to start a service directly.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>confirm whether a package exposes a service</li>
          <li>check whether a service is enabled</li>
          <li>copy the command used to start a service</li>
          <li>open details for a service before taking action</li>
        </ul>
        <p>The toolbar includes:</p>
        <ul>
          <li>a search field with the placeholder <strong>Search services (min 3 chars)...</strong></li>
          <li><strong>ALL</strong>, <strong>SYSTEM</strong>, and <strong>APP</strong> type filtering</li>
          <li><strong>Refresh</strong></li>
          <li><strong>Full Scan</strong></li>
          <li><strong>Show Disabled</strong></li>
        </ul>
        <p>Terms used here:</p>
        <ul>
          <li><strong>System</strong> service means the service is associated with system-level components.</li>
          <li><strong>App</strong> service means the service comes from an installed app.</li>
          <li><strong>Exported</strong> means Android may allow other apps or tools to address that service directly.</li>
        </ul>
        <p>What the controls do:</p>
        <ul>
          <li><strong>ALL</strong>, <strong>SYSTEM</strong>, and <strong>APP</strong> filter the list by service type.</li>
          <li><strong>Show Disabled</strong> includes disabled services.</li>
          <li><strong>Full Scan</strong> runs a broader scan across apps to find more service data.</li>
        </ul>
        <div className="help-note help-note-info">
          <strong>Note:</strong>
          <ul>
            <li><strong>Full Scan</strong> may take longer than a normal refresh. While it runs, Beep replaces the list with a scan progress dialog.</li>
            <li><strong>Start</strong> sends a start request to the device. A service still may not start if Android or the app blocks that request.</li>
          </ul>
        </div>
        <p>If a full scan is running, Beep shows <strong>Scanning Services</strong> with progress and the package currently being scanned. You can select <strong>Cancel</strong> to stop the scan.</p>
        <p>For each service, the available actions are:</p>
        <ul>
          <li><strong>View Details</strong></li>
          <li><strong>Start</strong></li>
          <li><strong>Copy Command</strong></li>
        </ul>
        <p>If <strong>Start</strong> appears to do nothing, that usually means the device or app rejected the request. In that case, review the service details, check whether the service is disabled, or use <strong>Copy Command</strong> for manual troubleshooting.</p>
      </>
    ),
  },
  {
    id: 'broadcasts',
    title: 'Broadcasts',
    keywords: ['broadcasts', 'intents', 'send', 'extras', 'broadcast receiver', 'manifest', 'runtime'],
    content: (
      <>
        <p>A broadcast is an Android intent message sent to apps or system components. Use <strong>Broadcasts</strong> when you need to inspect available broadcast actions, prepare a broadcast command, or send a broadcast with optional extras.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>search for a broadcast action by name</li>
          <li>group broadcasts by package or action</li>
          <li>prepare a command before sending it</li>
          <li>add extras to a broadcast</li>
        </ul>
        <p>The toolbar includes:</p>
        <ul>
          <li>a search field with the placeholder <strong>Search broadcasts (min 2 chars)...</strong></li>
          <li>a grouping menu:
            <ul>
              <li><strong>Group by Action</strong></li>
              <li><strong>Group by Package</strong></li>
            </ul>
          </li>
          <li><strong>Refresh</strong></li>
        </ul>
        <p>Grouping changes how the list is organized:</p>
        <ul>
          <li><strong>Group by Action</strong> groups broadcasts by intent action</li>
          <li><strong>Group by Package</strong> groups broadcasts by package</li>
        </ul>
        <p>If a broadcast is marked <strong>Requires Permission</strong>, Android may reject it unless the caller has the required permission.</p>
        <p>The list combines two sources. <strong>Runtime</strong> badges mark receivers currently registered on the device, and <strong>Manifest</strong> badges mark receivers apps declare in their manifest. A broadcast can show both badges when apps register and declare it. This is why the list can be longer than what standard ADB output shows.</p>
        <p>For each broadcast, the interface shows these actions:</p>
        <ul>
          <li><strong>Send</strong></li>
          <li><strong>Configure</strong></li>
          <li><strong>Copy Command</strong></li>
        </ul>
        <p>Use these actions this way:</p>
        <ul>
          <li><strong>Send</strong> is the direct action shown in the list.</li>
          <li><strong>Configure</strong> is the safer choice when you want to review details, add extras, and check the command before sending.</li>
          <li><strong>Copy Command</strong> copies a basic ADB command without sending anything.</li>
        </ul>
        <div className="help-note help-note-warning">
          <strong>Important:</strong> Sending a broadcast can trigger app-specific or device-specific behavior immediately, such as changing app state, triggering background tasks, or affecting system behavior. Some broadcasts may produce effects that cannot be undone in the moment. Use <strong>Configure</strong> to review the command before sending if you are unsure what the broadcast will do.
        </div>

        <h3 className="help-subsection-title">Send Broadcast</h3>
        <p>Use <strong>Send Broadcast</strong> when you need to add extras or confirm what Beep is about to send.</p>
        <p>The dialog shows:</p>
        <ul>
          <li><strong>Action</strong></li>
          <li><strong>Package</strong></li>
          <li>a permission warning when the broadcast requires one</li>
          <li><strong>Extras (String)</strong></li>
          <li><strong>Command Preview</strong></li>
        </ul>
        <p>Terms used here:</p>
        <ul>
          <li>an <strong>intent</strong> is the Android message being sent</li>
          <li><strong>extras</strong> are additional key-value values attached to that message</li>
          <li><strong>Command Preview</strong> shows the ADB command Beep will build from the values in the dialog</li>
        </ul>
        <p>In this dialog you can:</p>
        <ul>
          <li>add an extra</li>
          <li>enter a <strong>Key</strong> and <strong>Value</strong> for each extra</li>
          <li>remove an extra</li>
          <li>review the generated command in <strong>Command Preview</strong></li>
          <li>select <strong>Send</strong> to dispatch the broadcast</li>
        </ul>
        <p>If you are unsure whether a broadcast is correct, use <strong>Configure</strong> and review <strong>Command Preview</strong> before sending it.</p>
        <p>If sending a broadcast appears to do nothing, common reasons include:</p>
        <ul>
          <li>the broadcast requires a permission you do not have</li>
          <li>the target app expects different extras</li>
          <li>the device or app ignores that broadcast unless other conditions are met</li>
        </ul>
        <p>The current <strong>Send Broadcast</strong> dialog configures extras as string key-value pairs.</p>
      </>
    ),
  },
  {
    id: 'permissions',
    title: 'Permissions',
    keywords: ['permissions', 'dangerous', 'normal', 'runtime', 'signature', 'privileged'],
    content: (
      <>
        <p>Use <strong>Permissions</strong> when you want to inspect what permissions apps declare and how Android classifies those permissions.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>confirm whether an app declares a specific permission</li>
          <li>compare permission types across apps</li>
          <li>copy a permission name for a report or command</li>
        </ul>
        <p>The toolbar includes:</p>
        <ul>
          <li>a search field with the placeholder <strong>Search permissions (3+ chars)...</strong></li>
          <li><strong>Refresh</strong></li>
        </ul>
        <p>Below the toolbar, Beep provides filter switches:</p>
        <ul>
          <li><strong>Normal</strong></li>
          <li><strong>Dangerous</strong></li>
          <li><strong>Signature</strong></li>
          <li><strong>Privileged</strong></li>
          <li><strong>Instant</strong></li>
          <li><strong>Runtime</strong></li>
        </ul>
        <p>What these terms mean:</p>
        <ul>
          <li><strong>Normal</strong>: low-risk permissions Android grants automatically</li>
          <li><strong>Dangerous</strong>: permissions that protect sensitive data or device features</li>
          <li><strong>Signature</strong>: permissions usually limited to apps signed with the same certificate</li>
          <li><strong>Privileged</strong>: permissions normally reserved for privileged system-level software</li>
          <li><strong>Instant</strong>: permissions relevant to Android instant app behavior</li>
          <li><strong>Runtime</strong>: permissions Android can request and grant while the app is in use</li>
        </ul>
        <p>If search or filtering is active, Beep shows both the filtered count and the total count.</p>
        <p>Each permission row shows the permission name and applicable flags. Use <strong>Copy Permission Name</strong> to copy a permission exactly as shown.</p>
      </>
    ),
  },
  {
    id: 'automation',
    title: 'Automation',
    keywords: ['automation', 'scripts', 'workflow', 'repeatable', 'steps'],
    content: (
      <>
        <p>Use <strong>Automation</strong> when you want to save a repeatable sequence of actions instead of performing them one by one.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>build a repeatable setup or cleanup flow</li>
          <li>save a common test sequence</li>
          <li>rerun the same steps on another device</li>
        </ul>
        <p>Automation has two areas:</p>
        <ul>
          <li><strong>My Scripts</strong></li>
          <li>the script editor</li>
        </ul>

        <h3 className="help-subsection-title">My Scripts</h3>
        <p><strong>My Scripts</strong> lists saved scripts and provides these controls:</p>
        <ul>
          <li><strong>Search scripts...</strong></li>
          <li><strong>Import</strong></li>
          <li><strong>Create</strong></li>
        </ul>
        <p>Each script card shows:</p>
        <ul>
          <li>the script name</li>
          <li>the number of steps</li>
          <li>when it was last updated</li>
          <li><strong>Export script</strong></li>
          <li><strong>Duplicate script</strong></li>
          <li><strong>Delete script</strong></li>
        </ul>
        <p>If you do not have any saved scripts yet, Beep still shows <strong>Import</strong> and <strong>Create Script</strong> in the empty state so you can start from either direction.</p>

        <h3 className="help-subsection-title">Script editor</h3>
        <p>When you open a script, the editor shows:</p>
        <ul>
          <li><strong>Script name</strong></li>
          <li><strong>Save script</strong></li>
          <li><strong>Run Script</strong></li>
          <li><strong>Back to scripts</strong></li>
        </ul>
        <p>If a script is running or fails, the editor shows <strong>Execution Progress</strong> with the current step number and any error message.</p>
        <p>Each step appears in order and shows:</p>
        <ul>
          <li>the step description</li>
          <li>its execution status</li>
          <li>controls to move the step up or down</li>
          <li><strong>Remove step</strong></li>
        </ul>
        <p>Use <strong>Add Step</strong> to add a new step type.</p>

        <h3 className="help-subsection-title">Available step types</h3>
        <p>The current step selector offers these step types:</p>
        <ul>
          <li><strong>Install APK</strong></li>
          <li><strong>Start Activity</strong></li>
          <li><strong>Send Broadcast</strong></li>
          <li><strong>Clear Data</strong></li>
          <li><strong>Stop Package</strong></li>
          <li><strong>Remove Package</strong></li>
        </ul>
        <p>Depending on the step type, Beep opens a selection dialog:</p>
        <ul>
          <li><strong>Install APK</strong> opens a file picker</li>
          <li><strong>Start Activity</strong> opens <strong>Select Activity</strong></li>
          <li><strong>Send Broadcast</strong> opens <strong>Configure Broadcast</strong></li>
          <li><strong>Clear Data</strong>, <strong>Stop Package</strong>, and <strong>Remove Package</strong> open <strong>Select Package</strong></li>
        </ul>
        <p>Important distinctions:</p>
        <ul>
          <li><strong>Remove Package</strong> in <strong>Automation</strong> is the scripted version of uninstalling a package. Use it when you want the uninstall action saved as part of a repeatable script.</li>
          <li><strong>Uninstall</strong> in <strong>Packages</strong> is the immediate, one-off version of that action.</li>
          <li><strong>Stop Package</strong> stops a running app process.</li>
          <li><strong>Clear Data</strong> removes the app's stored data.</li>
        </ul>

        <h4 className="help-subsubsection-title">Select APK</h4>
        <p>When you add <strong>Install APK</strong>, Beep opens a macOS file picker so you can choose any <code className="help-code">.apk</code> file on your Mac.</p>
        <p>After you choose the file, Beep copies that APK into the script's own directory so the script can keep working later and can be exported with the APK included.</p>

        <h4 className="help-subsubsection-title">Import a script</h4>
        <p>To import a packaged script:</p>
        <ol>
          <li>Open <strong>Automation</strong>.</li>
          <li>Stay on <strong>My Scripts</strong>.</li>
          <li>Select <strong>Import</strong>.</li>
          <li>Choose a <code className="help-code">.beep</code> package.</li>
        </ol>
        <p>Beep restores the script into the scripts directory, including any packaged resources such as APK files.</p>
        <p>If the imported package contains a script with an <code className="help-code">id</code> that already exists locally, Beep warns you before it overwrites the existing script directory.</p>

        <h4 className="help-subsubsection-title">Export a script</h4>
        <p>To export a script:</p>
        <ol>
          <li>Open <strong>Automation</strong>.</li>
          <li>Stay on <strong>My Scripts</strong>.</li>
          <li>Find the script you want to package.</li>
          <li>Select <strong>Export script</strong> on that script card.</li>
          <li>Choose where to save the <code className="help-code">.beep</code> package.</li>
        </ol>
        <p>The exported package includes the full script directory structure. If the script contains <strong>Install APK</strong> steps, the packaged file includes the APK resources stored with that script.</p>

        <h4 className="help-subsubsection-title">Select Activity</h4>
        <p><strong>Select Activity</strong> groups activities by package. You can search with at least two characters and select an activity to add it as a step. Activities may be marked <strong>LAUNCHER</strong> or <strong>EXPORTED</strong>.</p>

        <h4 className="help-subsubsection-title">Select Package</h4>
        <p><strong>Select Package</strong> lists packages and lets you search by package name with at least two characters. After you choose a package, Beep adds the selected package to the step you are creating.</p>

        <h4 className="help-subsubsection-title">Configure Broadcast</h4>
        <p><strong>Configure Broadcast</strong> lets you define a broadcast-based automation step. It includes:</p>
        <ul>
          <li><strong>Action *</strong></li>
          <li><strong>Package (optional)</strong></li>
          <li><strong>Extras</strong></li>
          <li><strong>Preview</strong></li>
        </ul>
        <p>In this dialog, extras can use these value types:</p>
        <ul>
          <li><code className="help-code">STRING</code>: text</li>
          <li><code className="help-code">INT</code>: whole numbers</li>
          <li><code className="help-code">BOOLEAN</code>: <code className="help-code">true</code> or <code className="help-code">false</code></li>
          <li><code className="help-code">LONG</code>: larger whole numbers</li>
          <li><code className="help-code">FLOAT</code>: decimal numbers</li>
          <li><code className="help-code">URI</code>: address-style values</li>
        </ul>
        <p>Beep enables <strong>OK</strong> only when <strong>Action *</strong> is not empty.</p>

        <h3 className="help-subsection-title">Run a script</h3>
        <p>To run a script:</p>
        <ol>
          <li>Select a device in the main device selector.</li>
          <li>Open <strong>Automation</strong>.</li>
          <li>Open an existing script or create a new one.</li>
          <li>Add at least one step.</li>
          <li>Select <strong>Run Script</strong>.</li>
        </ol>
        <p><strong>Run Script</strong> is available only when a device is selected and the script contains at least one step.</p>
        <p>If a scripted action appears to do nothing, the same Android restrictions that affect manual actions may also affect the script.</p>
      </>
    ),
  },
  {
    id: 'device-settings',
    title: 'Device Settings',
    keywords: ['device settings', 'settings', 'system', 'global', 'secure', 'toggle', 'edit', 'airplane mode'],
    content: (
      <>
        <p>Android keeps device settings as key-value entries in three tables: <strong>system</strong>, <strong>global</strong>, and <strong>secure</strong>. Use <strong>Device Settings</strong>, the <strong>Settings</strong> item in the sidebar, when you need to look up or change these values without typing <code className="help-code">adb shell settings</code> commands by hand.</p>
        <p>This is not the same as the <strong>Device Settings</strong> button in <strong>Device Info</strong>, which opens the device's own Settings app on the device screen.</p>
        <p>Typical tasks:</p>
        <ul>
          <li>check the current value of a setting, such as <code className="help-code">screen_off_timeout</code> or <code className="help-code">airplane_mode_on</code></li>
          <li>read what a setting does before changing it</li>
          <li>toggle a boolean setting or pick from a known list of options</li>
          <li>change a free-form value such as a timeout or a device name</li>
        </ul>
        <p>Beep loads every setting from all three tables into one list, sorted alphabetically by key. A typical device has around 300 settings.</p>

        <h3 className="help-subsection-title">What each row shows</h3>
        <ul>
          <li>the <strong>key</strong>, which you can copy</li>
          <li>the <strong>current value</strong>, shown as <strong>not set</strong> when the key exists without a value</li>
          <li>a <strong>table tag</strong> naming the table the setting came from: <strong>system</strong>, <strong>global</strong>, or <strong>secure</strong></li>
          <li>metadata: settings documented in AOSP show their value type and a description of what they do; vendor, OEM, carrier, and unknown settings show <strong>Custom setting</strong></li>
        </ul>
        <p>The table tag matters because the same key name can exist in more than one table with different values.</p>

        <h3 className="help-subsection-title">Finding settings</h3>
        <p>The toolbar includes:</p>
        <ul>
          <li>a search field that starts filtering once you type at least two characters, matching both keys and values</li>
          <li><strong>Group by table</strong>, a toggle that reorganizes the list into System, Global, and Secure sections</li>
          <li><strong>Refresh</strong>, which reloads all three tables from the device</li>
        </ul>

        <h3 className="help-subsection-title">Editing settings</h3>
        <p>Every edit writes to the device immediately with the <code className="help-code">settings put</code> command. The editor you get depends on the setting:</p>
        <ul>
          <li><strong>Switch settings</strong>: AOSP settings whose value is <code className="help-code">0</code> or <code className="help-code">1</code> show a switch. Toggling it applies the new value immediately.</li>
          <li><strong>Dropdown settings</strong>: AOSP settings with a small documented set of options show a dropdown. Picking an option applies it immediately.</li>
          <li><strong>Text settings</strong>: everything else shows a text box. <strong>Save</strong> and <strong>Reset</strong> appear only while the typed value differs from the device value: <strong>Save</strong> writes the value to the device, and <strong>Reset</strong> restores the text box to the current device value.</li>
        </ul>
        <p>After every successful write, Beep reads the value back from the device and updates the row, so the row always shows what the device actually stored. Devices may adjust values, so the stored value can differ from what you entered.</p>
        <p>If a write fails, Beep shows an error message. Switches and dropdowns return to their previous value, and text edits keep your draft so you can retry.</p>
        <div className="help-note help-note-warning">
          <strong>Important:</strong> Changes take effect on the device immediately, and Beep does not ask for confirmation. Changing system settings can alter device behavior, including kiosk flags and provisioning state. If you are not sure what a setting does, read its description first and leave it unchanged.
        </div>
      </>
    ),
  },
  {
    id: 'troubleshooting',
    title: 'Troubleshooting',
    keywords: ['troubleshooting', 'problems', 'errors', 'unavailable', 'not working', 'unauthorized', 'offline', 'update failed'],
    content: (
      <>
        <h3 className="help-subsection-title">When Something Is Unavailable</h3>
        <p>If a panel is empty or an action is not useful yet, the usual causes are:</p>
        <ul>
          <li>no device is selected</li>
          <li>ADB is not configured correctly</li>
          <li>the device has not reported that type of data yet</li>
          <li>Android or the target app restricts the action</li>
        </ul>
        <p>Examples:</p>
        <ul>
          <li>missing activities may mean the app does not expose startable activities</li>
          <li>missing services or broadcasts may mean the device did not report any for the selected package or search</li>
          <li>a required permission warning on a broadcast means Android may block that broadcast</li>
          <li>a service start or broadcast send may appear to do nothing because the app or device rejected it</li>
          <li>a missing Storage, Modem, GNSS, or NFC tab in <strong>Device Info</strong> means the device does not have or did not report that hardware</li>
          <li>an <strong>N/A</strong> value in the Battery or Storage tab means the device does not expose that value, not that Beep failed to read it</li>
          <li>a device settings edit that snaps back to its previous value means the device rejected the write</li>
        </ul>
        <p>If you are unsure what to do next:</p>
        <ol>
          <li>confirm the correct device is selected</li>
          <li>use <strong>Refresh</strong></li>
          <li>reduce or clear the search text</li>
          <li>review whether the action requires permissions or app-specific conditions</li>
          <li>copy the command for manual troubleshooting if available</li>
        </ol>

        <h3 className="help-subsection-title">Device connection states</h3>
        <p>When a device is connected but not ready, Beep shows a banner above the panels. Each state has a next step:</p>
        <ul>
          <li><strong>ADB missing or invalid</strong>: open <strong>Settings</strong> and set a valid ADB path. Beep does not continue past startup until the path is valid.</li>
          <li><strong>No device</strong>: confirm the cable and that the device is unlocked, then select <strong>Refresh devices</strong>.</li>
          <li><strong>Unauthorized</strong>: unlock the device and accept the <strong>Allow USB debugging</strong> prompt, then select <strong>Refresh devices</strong>.</li>
          <li><strong>Offline</strong>: the device is visible but not responding. Reconnect it or reboot it, then select <strong>Refresh devices</strong>.</li>
        </ul>

        <h3 className="help-subsection-title">If an update fails</h3>
        <ul>
          <li>a <strong>Download Failed</strong> dialog offers <strong>Retry Download</strong> or <strong>Continue Current Version</strong>; check your internet connection before retrying</li>
          <li>if Beep reports that the downloaded file failed verification, the file is deleted and nothing is installed; update again to re-download it</li>
          <li>if installation fails, the downloaded file is kept on your Mac so you can retry or install it manually</li>
          <li>Beep only checks for updates after ADB is configured, so a blocked or invalid ADB setup also means no update check</li>
        </ul>
      </>
    ),
  },
  {
    id: 'glossary',
    title: 'Glossary',
    keywords: ['terms', 'definitions', 'glossary', 'vocabulary', 'emmc', 'ufs', 'gnss', 'hal', 'ril', 'nci', 'supl', 'ttff', 'selinux', 'treble', 'verified boot'],
    content: (
      <>
        <dl className="help-glossary">
          <dt>Activity</dt>
          <dd>An Android screen or entry point that can be launched.</dd>

          <dt>ADB</dt>
          <dd>Android Debug Bridge, the tool Beep uses to talk to the device.</dd>

          <dt>Broadcast</dt>
          <dd>An Android intent message sent to apps or system components.</dd>

          <dt>Command Preview</dt>
          <dd>The ADB command Beep builds from the values you entered, shown before you send it.</dd>

          <dt>Debug mode</dt>
          <dd>A launch mode intended for debugging workflows rather than normal app use.</dd>

          <dt>eMMC</dt>
          <dd>Embedded MultiMediaCard, a type of internal flash storage common in Android devices. Beep shows chip-level details such as manufacturer, model, serial, firmware, and health status when the device uses eMMC storage.</dd>

          <dt>Exported</dt>
          <dd>A component Android may allow other apps or tools to address directly.</dd>

          <dt>Extras</dt>
          <dd>Additional key-value values attached to a broadcast intent.</dd>

          <dt>GNSS</dt>
          <dd>Global Navigation Satellite System. The hardware and software that provides GPS and satellite-based positioning on Android devices.</dd>

          <dt>HAL</dt>
          <dd>Hardware Abstraction Layer. An Android interface that connects the operating system to vendor-specific hardware drivers.</dd>

          <dt>Health Status</dt>
          <dd>A storage health indicator for eMMC chips based on the device-reported lifetime estimate, shown as Good, Fair, or Worn. Not available for UFS or unknown storage types.</dd>

          <dt>LPP</dt>
          <dd>LTE Positioning Protocol. A standard used in cellular networks for determining device location, shown as profile badges in the GNSS tab.</dd>

          <dt>NCI</dt>
          <dd>NFC Controller Interface. The standard protocol for communication between the host processor and the NFC controller chip.</dd>

          <dt>Package</dt>
          <dd>An installed Android app identified by its package name, such as <code className="help-code">com.example.app</code>.</dd>

          <dt>Permission</dt>
          <dd>An Android capability an app declares or requests, such as access to the camera or contacts.</dd>

          <dt>Privileged permission</dt>
          <dd>A permission normally reserved for privileged or system-level software.</dd>

          <dt>RIL</dt>
          <dd>Radio Interface Layer. The Android layer that connects the telephony framework to the modem hardware.</dd>

          <dt>Runtime permission</dt>
          <dd>A permission Android can grant while the app is in use instead of only at install time.</dd>

          <dt>Script folder</dt>
          <dd>The local directory where Beep stores a script and its packaged resources, such as APK files chosen for <strong>Install APK</strong> steps.</dd>

          <dt>SELinux</dt>
          <dd>Security-Enhanced Linux. An Android security mechanism that enforces access control policies, shown as Enforcing, Permissive, or Disabled.</dd>

          <dt>Service</dt>
          <dd>An Android component that performs work without presenting a normal screen.</dd>

          <dt>Signing certificate</dt>
          <dd>The certificate used to sign an app build, useful for confirming build identity.</dd>

          <dt>SUPL</dt>
          <dd>Secure User Plane Location. A protocol that helps Android devices determine their position using cellular network data.</dd>

          <dt>System property</dt>
          <dd>A key-value setting reported by Android for device and system configuration.</dd>

          <dt>target SDK</dt>
          <dd>The Android API level the app targets.</dd>

          <dt>Treble</dt>
          <dd>Project Treble. An Android architecture that separates the vendor implementation from the Android framework, shown as Yes or No in the System tab.</dd>

          <dt>TTFF</dt>
          <dd>Time To First Fix. The time it takes for a GNSS receiver to acquire satellite signals and calculate a position fix.</dd>

          <dt>UFS</dt>
          <dd>Universal Flash Storage, a faster type of internal flash storage. Beep shows only the interface type and capacity for UFS chips because they expose fewer diagnostic fields than eMMC.</dd>

          <dt>Verified Boot</dt>
          <dd>Android's boot chain verification mechanism that ensures device software has not been tampered with, shown as Green, Yellow, Orange, or Red.</dd>
        </dl>
      </>
    ),
  },
];

// Create flat list for search indexing
export const searchableContent = helpSections.map(section => ({
  id: section.id,
  title: section.title,
  keywords: section.keywords.join(' '),
  // Extract text content from React nodes for searching
  content: extractTextContent(section.content),
}));

// Helper function to extract text from React nodes
function extractTextContent(node: React.ReactNode): string {
  if (typeof node === 'string') return node;
  if (typeof node === 'number') return node.toString();
  if (!node) return '';
  
  if (Array.isArray(node)) {
    return node.map(extractTextContent).join(' ');
  }
  
  if (React.isValidElement(node)) {
    const props = node.props as any;
    return extractTextContent(props.children);
  }
  
  return '';
}
